import React, { useEffect, useState } from "react";
import "react-day-picker/dist/style.css";
import { MdArrowForward, MdCheck } from "react-icons/md";
import { useNavigate } from "react-router";
import { FINALIZE_PAGE } from "../../../routes";
import { Assort } from "../../../models/Assort";
import { TicketAccess } from "../../../models/TicketAccess";
import useBasketManager from "../../FinalizePage/hooks/useBasketManager";
import usePreBasket from "../../Universal/hooks/usePreBasket";
import PreBasketSummary, { CalendarSummaryRows, PreBasketSummaryContext } from "../../Universal/PreBasketSummary";
import TicketLineSummary from "../../Universal/TicketLineSummary";
import "./style.scss";
import { BasketItem } from "../../../models/BasketItem";
import { useTranslation } from "react-i18next";
import CalendarPassReservaton from "./calendarPassReservation";
import { Exercise } from "../../../models/Exercise";
import useFacility from "../../../../utils/hooks/useFacility";
import useCalculatePreBasketQnt from "../../Universal/hooks/useCalculatePreBasketQnt";
import { QntStateItem } from "../../../models/QntStateItem";
import EASkeletonLoader from "../../shared/EASkeletonLoader";
import EAButton from "../../shared/Buttons/EAButton";
import useCustomOffer from "../../CustomOffer/hooks/useCustomOffer";
import getId from "../../../../utils/getId";
import { isKarnetTestFacility } from "../../CourtGrid/components/CourtReservationModal/CourtTicketSelection/karnetConcept";
import KarnetSection from "../../CourtGrid/components/CourtReservationModal/CourtTicketSelection/KarnetSection";
import KarnetTermsConfirmation from "../../CourtGrid/components/CourtReservationModal/CourtTicketSelection/KarnetTermsConfirmation";
import useCalendarKarnetPurchase from "./useCalendarKarnetPurchase";

function pictureUrl(url?: string): string | undefined {
  return url && url.length > 1 ? url : undefined;
}

function formatTrainer(details?: Exercise): { name: string; photo?: string } | undefined {
  if (!details?.humanName || details.humanName.length <= 1) return undefined;
  if (!getId(details.deputyHumanIds)) {
    return { name: details.humanName, photo: pictureUrl(details.humanPicture) };
  }

  const deputy = [details.deputyHumanName, details.deputyHumanLastName].filter(Boolean).join(" ");
  return {
    name: deputy ? `${details.humanName} → ${deputy}` : details.humanName,
    photo: pictureUrl(details.deputyHumanPicture),
  };
}

interface IProps {
  tickets: Assort[];
  ticketAccess: TicketAccess;
  ticketLimit: number;
  refreshCapacityAfterAddTicket: () => void;
  exerciseDetails?: Exercise;
  joinToExerciseByPass: (exerciseIds: string, assignType: boolean, passId: number) => void;
  alertCalendar: string;
  // Opcjonalne propsy dla limitów asortymentowych (GET_EXERCISE_ASSORT_LIMITS)
  getLimitForAssort?: (assortId: string, exerciseGroupId: string) => number | null;
  isFallback?: boolean;
  isLimitsLoading?: boolean;
  globalLimitOverride?: number;
  exerciseGroupCompositeId?: string; // format: "objectDb,objectId" grupy zajęciowej
  /** Kolumna zablokowana (np. brak wybranej godziny): ceny widać, liczniki są nieaktywne. */
  locked?: boolean;
  /** Wybrany termin pokazywany w podsumowaniu nad sumą. */
  summaryContext?: PreBasketSummaryContext;
  /** „Kup kolejne bilety" – np. powrót na początek ścieżki (wybór daty). Bez tego wracamy tylko do liczników. */
  onBuyMore?: () => void;
}
const CalendarAssorts: React.FC<IProps> = ({
  tickets,
  ticketLimit,
  ticketAccess,
  refreshCapacityAfterAddTicket,
  joinToExerciseByPass,
  exerciseDetails,
  alertCalendar,
  getLimitForAssort,
  isFallback,
  isLimitsLoading,
  globalLimitOverride,
  exerciseGroupCompositeId,
  locked = false,
  summaryContext,
  onBuyMore,
}: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const { preBasket, addItemToBasket, removeItemFromBasket, clearBasket, setItemQuantity } = usePreBasket();
  const { getBasket } = useBasketManager();
  const [, setAllowToAssign] = useState(false);
  const facilityIds = useFacility();
  const { qntState } = useCalculatePreBasketQnt(preBasket);
  const { getSaleStepByStepId } = useCustomOffer();
  const foundedSaleStep = getSaleStepByStepId();
  const saleStepAllowToSignByPass = foundedSaleStep?.allowToSignByPass;

  const trainer = formatTrainer(exerciseDetails);
  const summary: PreBasketSummaryContext = {
    ...summaryContext,
    trainer: trainer?.name,
    trainerPhoto: trainer?.photo,
  };

  const handleDeleteBasket = () => {
    clearBasket();

    setTimeout(() => {
      getBasket();
    }, 200);
    setTimeout(() => {
      refreshCapacityAfterAddTicket();
    }, 300);
  };

  const hasKarnetEligibleInBasket = isKarnetTestFacility(facilityIds) && preBasket.some((item) => item.qnt > 0);
  const karnet = useCalendarKarnetPurchase({
    exerciseDetails: exerciseDetails ?? null,
    anchorTicketAccess: ticketAccess,
    preBasket,
    onPurchased: handleDeleteBasket,
  });
  const sumBasket = () => preBasket.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);
  const ticketsInCurrentBasket = () => {
    if (qntState && qntState.length > 0) {
      return qntState
        .filter((item) => item.exerciseIds === ticketAccess.exerciseIds)
        .reduce((acc: number, item: QntStateItem) => acc + item.count, 0);
    } else {
      return preBasket.reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);
    }
  };

  const asignByPass = (assigned: boolean, passId: number) => {
    joinToExerciseByPass(ticketAccess.exerciseIds, assigned, passId);
  };

  // Zmiana terminu (inna godzina) wraca do wyboru biletów.
  useEffect(() => {
    setAdded(false);
  }, [ticketAccess.exerciseIds]);

  useEffect(() => {
    if (facilityIds === "1212" || facilityIds === "1246") {
      setAllowToAssign(true);
    }
    //eslint-disable-next-line
  }, [facilityIds]);

  // Gdy limity asortymentowe są załadowane i nie ma fallbacku, używamy globalLimitOverride
  const useLimitsApi = isFallback === false && !isLimitsLoading && globalLimitOverride !== undefined;
  const effectiveLimit = useLimitsApi ? globalLimitOverride! : ticketLimit;
  // W podglądzie (brak wybranej godziny) pojemność jest nieznana – nie pokazujemy limitów ani „niedostępne".
  const applyLimits = !locked;

  if (karnet.step === "confirm") {
    return (
      <KarnetTermsConfirmation
        terms={karnet.selectedTerms}
        availableCount={karnet.availableTerms.length}
        unavailableCount={karnet.unavailableCount}
        totalPrice={sumBasket() * karnet.availableTerms.length}
        isAdding={karnet.isAdding}
        addError={karnet.addError}
        onConfirm={karnet.confirmPurchase}
        onBack={karnet.backToSelect}
        variant="flow"
      />
    );
  }

  return (
    <>
      {tickets.length > 0 ? (
        <>
          <fieldset className="calendar-sale-tickets" disabled={locked}>
          {tickets.map((item, index) => {
            const assortLimit =
              applyLimits && useLimitsApi && getLimitForAssort && exerciseGroupCompositeId
                ? getLimitForAssort(getId(item), exerciseGroupCompositeId)
                : undefined;
            const isUnavailable = assortLimit === 0;

            return (
              <div
                className={`calendar-sale-ticket${isUnavailable ? " ticket-block--unavailable" : ""}`}
                key={index}
              >
                <TicketLineSummary
                  item={item}
                  exercise={ticketAccess}
                  preBasket={preBasket}
                  addItemToBasket={addItemToBasket}
                  removeItemFromBasket={removeItemFromBasket}
                  setItemQuantity={setItemQuantity}
                  ticketLimit={!applyLimits || useLimitsApi ? undefined : ticketLimit}
                  eventCapacityLimit={applyLimits && useLimitsApi ? globalLimitOverride : undefined}
                  assortSpecificLimit={assortLimit}
                  qntState={qntState}
                  allowManualInput={true}
                  variant="roomy"
                />
              </div>
            );
          })}
          </fieldset>

          {applyLimits && effectiveLimit === ticketsInCurrentBasket() && (
            <div className="flex-between-center alert alert-red ">
              <b>{t("nie_mozesz_dodac_wiekszej_ilosci_biletow_do_koszyka")}</b>
            </div>
          )}
          {exerciseDetails && (
            <>
              {saleStepAllowToSignByPass && (
                <CalendarPassReservaton
                  exerciseDetails={exerciseDetails}
                  asignByPass={asignByPass}
                  alertCalendar={alertCalendar}
                />
              )}
            </>
          )}

        </>
      ) : (
        <EASkeletonLoader height="100px" />
      )}

      {hasKarnetEligibleInBasket && (
        <KarnetSection
          enabled={karnet.enabled}
          onToggle={karnet.toggleEnabled}
          clampedRepeats={karnet.clampedRepeats}
          onRepeatsChange={karnet.setRepeats}
          horizonCount={karnet.schedule.horizonCount}
          lastAvailableDateLabel={karnet.schedule.lastAvailableDateLabel}
          loading={karnet.schedule.loading}
          perTermPrice={sumBasket()}
        />
      )}

      {karnet.enabled ? (
        <div className="pre-basket-summary__container pre-basket-summary--calendar">
          <CalendarSummaryRows context={summary} />
          <div className="pre-basket-summary__total">
            <span>{t("razem")}</span>
            <b>
              {(sumBasket() * karnet.clampedRepeats).toFixed(2)} {t("zl")}
            </b>
          </div>
          <EAButton
            title="Dalej — sprawdź terminy"
            onClick={karnet.goToConfirmation}
            endIcon={<MdArrowForward />}
            disabled={karnet.schedule.loading || karnet.clampedRepeats < 1}
            fullWidth
          />
        </div>
      ) : added ? (
        <div className="calendar-sale-added" role="status">
          <span className="calendar-sale-added__icon" aria-hidden={true}>
            <MdCheck />
          </span>
          <h3>{t("dodano_do_koszyka")}</h3>
          <EAButton
            title={t("przejdz_do_koszyka")}
            endIcon={<MdArrowForward />}
            onClick={() => navigate(FINALIZE_PAGE)}
            fullWidth
          />
          <EAButton title={t("kalendarz_kup_kolejne_bilety")} variant="outlined" onClick={() => (onBuyMore ? onBuyMore() : setAdded(false))} fullWidth />
        </div>
      ) : (
        <PreBasketSummary
          ticketLimit={effectiveLimit}
          preBasket={preBasket}
          clearBasket={handleDeleteBasket}
          qntState={qntState}
          variant="calendar"
          context={summary}
          onAdded={() => setAdded(true)}
        />
      )}
    </>
  );
};

export default CalendarAssorts;
